import React from "react";
import * as Icons from "lucide-react";
import { ArrowUpRight, Star, Quote } from "lucide-react";
import { STEPS, RESULTS, REVIEWS } from "../../mock";
import Reveal from "./Reveal";
import { SectionHead } from "./Services";

export const Process = () => (
  <section id="process" className="bg-[#08090b] py-24 lg:py-28">
    <div className="container-x">
      <SectionHead badge="Simple From Start To Finish" title="How It Works" sub="Honest guidance, careful tuning and a hassle-free process." />
      <div className="relative grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="absolute left-[12%] right-[12%] top-[52px] hidden h-px bg-gradient-to-r from-transparent via-brand/40 to-transparent lg:block" />
        {STEPS.map((s, i) => {
          const Icon = Icons[s.icon];
          return (
            <Reveal key={s.n} delay={i * 110}>
              <div className="group relative text-center" data-testid={`step-${i}`}>
                <div className="relative mx-auto flex h-[104px] w-[104px] items-center justify-center rounded-full border border-white/10 bg-[#111217] transition-colors group-hover:border-brand/60">
                  <Icon className="h-8 w-8 text-brand transition-transform duration-300 group-hover:scale-110" />
                  <span className="absolute -right-1 -top-1 flex h-9 w-9 items-center justify-center rounded-full bg-brand font-display text-sm font-bold text-white">{s.n}</span>
                </div>
                <h3 className="mt-7 font-display text-[22px] font-bold text-white">{s.title}</h3>
                <p className="mx-auto mt-3 max-w-[260px] text-[14.5px] leading-relaxed text-white/55">{s.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const Results = () => (
  <section id="results" className="bg-[#0d0e12] py-24 lg:py-28">
    <div className="container-x">
      <SectionHead badge="The Difference" title="More Power. Better Drive." sub="Real customer cars tuned in our workshop." />
      <div className="flex flex-wrap justify-center gap-5">
        {RESULTS.map((r, i) => (
          <Reveal key={r.car} delay={(i % 3) * 100} className="w-full md:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]">
            <div className="card-hover group overflow-hidden rounded-2xl border border-white/10 bg-[#111217]" data-testid={`result-card-${i}`}>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={r.img} alt={`${r.car} tuned by EvtTuning`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111217] via-[#111217]/30 to-transparent" />
                <div className="absolute bottom-4 left-5">
                  <h3 className="font-display text-xl font-bold text-white">{r.car}</h3>
                  <p className="text-[13px] text-white/70">{r.spec}</p>
                </div>
              </div>
              <div className="px-6 pb-6 pt-5">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">Before</div>
                    <div className="mt-1 font-display text-3xl font-bold leading-none text-white/85" data-testid={`result-before-bhp-${i}`}>{r.before}<span className="ml-1 text-[11px] font-semibold tracking-[0.08em] text-white/45">BHP</span></div>
                    <div className="mt-1.5 font-display text-[17px] font-semibold leading-none text-white/55" data-testid={`result-before-nm-${i}`}>{r.torqueBefore}<span className="ml-1 text-[10px] font-semibold tracking-[0.08em] text-white/35">NM</span></div>
                  </div>
                  <ArrowUpRight className="mb-6 h-5 w-5 text-brand transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
                  <div className="text-right">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-brand">After</div>
                    <div className="mt-1 font-display text-3xl font-bold leading-none text-brand" data-testid={`result-after-bhp-${i}`}>{r.after}<span className="ml-1 text-[11px] font-semibold tracking-[0.08em] text-white/45">BHP</span></div>
                    <div className="mt-1.5 font-display text-[17px] font-semibold leading-none text-brand/80" data-testid={`result-after-nm-${i}`}>{r.torqueAfter}<span className="ml-1 text-[10px] font-semibold tracking-[0.08em] text-white/35">NM</span></div>
                  </div>
                </div>
                <div className="mt-5 space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="w-9 text-[10px] font-semibold tracking-[0.08em] text-white/40">BHP</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-brand" style={{ width: `${(r.before / r.after) * 100}%` }} /></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-9 text-[10px] font-semibold tracking-[0.08em] text-white/40">NM</span>
                    <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-brand/60" style={{ width: `${(r.torqueBefore / r.torqueAfter) * 100}%` }} /></div>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 border-t border-white/10 pt-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.1em] text-white/45">Power gain</div>
                    <div className="mt-0.5 font-semibold text-white" data-testid={`result-gain-bhp-${i}`}>+{r.after - r.before} BHP <span className="text-xs font-medium text-emerald-400">+{Math.round(((r.after - r.before) / r.before) * 100)}%</span></div>
                  </div>
                  <div className="border-l border-white/10 pl-4 text-right">
                    <div className="text-[11px] uppercase tracking-[0.1em] text-white/45">Torque gain</div>
                    <div className="mt-0.5 font-semibold text-white" data-testid={`result-gain-nm-${i}`}>+{r.torqueAfter - r.torqueBefore} Nm <span className="text-xs font-medium text-emerald-400">+{Math.round(((r.torqueAfter - r.torqueBefore) / r.torqueBefore) * 100)}%</span></div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Reviews = () => (
  <section id="reviews" className="relative overflow-hidden bg-[#08090b] py-24 lg:py-28">
    <div className="pointer-events-none absolute -right-40 bottom-0 h-[500px] w-[500px] rounded-full bg-brand/10 blur-[120px]" />
    <div className="container-x relative">
      <SectionHead badge="Driver Reviews" title="Trusted By Local Drivers" sub="Rated 5 stars by drivers across North West London" />
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={(i % 3) * 90}>
            <div className="card-hover flex h-full flex-col rounded-2xl border border-white/10 bg-[#111217] p-6" data-testid={`review-card-${i}`}>
              <Quote className="h-8 w-8 rotate-180 text-brand/40" />
              <div className="mt-4 flex gap-1">{[...Array(5)].map((_, k) => <Star key={k} className="h-4 w-4 fill-brand text-brand" />)}</div>
              <p className="mt-4 flex-1 text-[15px] leading-relaxed text-white/80">"{r.text}"</p>
              <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/15 font-display text-lg font-bold text-brand">{r.name[0]}</div>
                <div>
                  <div className="text-[15px] font-semibold text-white">{r.name}</div>
                  <div className="text-[12.5px] text-white/45">{r.car}</div>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
